import { keepPreviousData, queryOptions } from "@tanstack/react-query";

import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";
import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import { etTodayDate } from "./retellCallsTheme";
import {
  type RetellCallsCareSpaceBreakdownResponse,
  type RetellCallsSnapshotParams,
  type RetellCallsSnapshotResponse,
  type RetellCallsSummaryResponse,
  type RetellCallsTransferReportResponse,
  isRetellCallsCareSpaceBreakdownResponse,
  isRetellCallsSnapshotResponse,
  isRetellCallsSummaryResponse,
  isRetellCallsTransferReportResponse,
} from "./retellCallsTypes";

export const retellCallsQueryKeys = {
  all: [...operatorsQueryKeys.all, "dashboards", "retell-calls"] as const,
  summary: (params: RetellCallsSnapshotParams) =>
    [...retellCallsQueryKeys.all, "summary", params] as const,
  transferReport: (params: RetellCallsSnapshotParams) =>
    [...retellCallsQueryKeys.all, "transfer-report", params] as const,
  careSpaceBreakdown: (params: RetellCallsSnapshotParams) =>
    [...retellCallsQueryKeys.all, "carespace-breakdown", params] as const,
  snapshot: (params: RetellCallsSnapshotParams) =>
    [...retellCallsQueryKeys.all, "snapshot", params] as const,
};

function throwIfUnauthorized(response: Response): void {
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
}

async function throwIfForbidden(response: Response, fallbackMessage: string): Promise<void> {
  if (response.status !== 403) {
    return;
  }
  const message = await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage);
  throw new OperatorsForbiddenError(message || fallbackMessage);
}

export async function fetchRetellCallsSnapshot(
  params: RetellCallsSnapshotParams,
): Promise<RetellCallsSnapshotResponse> {
  return fetchRetellCallsPart(
    "snapshot",
    params,
    isRetellCallsSnapshotResponse,
    "Retell Calls response was malformed",
  );
}

async function fetchRetellCallsPart<T>(
  path: string,
  params: RetellCallsSnapshotParams,
  guard: (value: unknown) => value is T,
  malformedMessage: string,
): Promise<T> {
  const search = new URLSearchParams({
    start: params.start,
    end: params.end,
  });
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/retell-calls/${path}?${search}`,
    { credentials: "include" },
  );
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load Retell Calls dashboard",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!guard(body)) throw new Error(malformedMessage);
  return body;
}

export function fetchRetellCallsSummary(
  params: RetellCallsSnapshotParams,
): Promise<RetellCallsSummaryResponse> {
  return fetchRetellCallsPart(
    "summary",
    params,
    isRetellCallsSummaryResponse,
    "Retell Calls summary response was malformed",
  );
}

export function fetchRetellCallsTransferReport(
  params: RetellCallsSnapshotParams,
): Promise<RetellCallsTransferReportResponse> {
  return fetchRetellCallsPart(
    "transfer-report",
    params,
    isRetellCallsTransferReportResponse,
    "Retell Calls transfer response was malformed",
  );
}

export function fetchRetellCallsCareSpaceBreakdown(
  params: RetellCallsSnapshotParams,
): Promise<RetellCallsCareSpaceBreakdownResponse> {
  return fetchRetellCallsPart(
    "carespace-breakdown",
    params,
    isRetellCallsCareSpaceBreakdownResponse,
    "Retell Calls CareSpace response was malformed",
  );
}

const STALE_TIME_TODAY_MS = 15 * 60_000;
const STALE_TIME_PAST_MS = 12 * 60 * 60_000;

/** Mirrors cacheTtlMsFor in the control plane: past ranges never change, today's still can. */
function staleTimeFor(end: string): number {
  return end.slice(0, 10) < etTodayDate() ? STALE_TIME_PAST_MS : STALE_TIME_TODAY_MS;
}

export function retellCallsSnapshotQueryOptions(params: RetellCallsSnapshotParams) {
  return queryOptions({
    queryKey: retellCallsQueryKeys.snapshot(params),
    queryFn: () => fetchRetellCallsSnapshot(params),
    staleTime: staleTimeFor(params.end),
    placeholderData: keepPreviousData,
  });
}

export function retellCallsSummaryQueryOptions(params: RetellCallsSnapshotParams) {
  return queryOptions({
    queryKey: retellCallsQueryKeys.summary(params),
    queryFn: () => fetchRetellCallsSummary(params),
    staleTime: staleTimeFor(params.end),
    placeholderData: keepPreviousData,
  });
}

export function retellCallsTransferReportQueryOptions(
  params: RetellCallsSnapshotParams,
  enabled = true,
) {
  return queryOptions({
    queryKey: retellCallsQueryKeys.transferReport(params),
    queryFn: () => fetchRetellCallsTransferReport(params),
    staleTime: staleTimeFor(params.end),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function retellCallsCareSpaceBreakdownQueryOptions(
  params: RetellCallsSnapshotParams,
  enabled = true,
) {
  return queryOptions({
    queryKey: retellCallsQueryKeys.careSpaceBreakdown(params),
    queryFn: () => fetchRetellCallsCareSpaceBreakdown(params),
    staleTime: staleTimeFor(params.end),
    placeholderData: keepPreviousData,
    enabled,
  });
}
